import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { ModalController } from '@ionic/angular';
import { forkJoin, map, of, switchMap, take } from 'rxjs';
import { ModalDailyReportComponent } from 'src/app/components/modal-daily-report/modal-daily-report.component';
import { ModalUploaderComponent } from 'src/app/components/model-uploader/modal-uploader.component';
import { PoViewerComponent } from 'src/app/components/po-viewer/po-viewer.component';
import { DataService } from 'src/app/services/data/data.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import { CPBillingCode } from 'src/app/services/types/cp-billing-code.model';
import { Crew } from 'src/app/services/types/crew.model';
import { DailyReport } from 'src/app/services/types/daily-report.model';
import { FileMeta } from 'src/app/services/types/file-meta.model';
import { JobDetails } from 'src/app/services/types/job-details.model';
import { OSP } from 'src/app/services/types/osp.model';
import { Task } from 'src/app/services/types/task.model';

@Component({
  selector: 'daily-importer-v3',
  templateUrl: './daily-importer-v3.page.html',
  styleUrls: ['./daily-importer-v3.page.scss'],
  providers: [DataService, DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DailyImporterV3Page implements OnInit {
  ospsLoaded: boolean = false;
  crewListLoaded: boolean = false;
  drLoaded: boolean = false;
  bcLoaded: boolean = false;

  ospList: OSP[];
  crewList: Crew[];
  dailyReports: DailyReport[];
  billingCodes: CPBillingCode[];
  tasks: Task[] = [];
  CPID?: number;
  CPName?: string;

  guid: string = 'pett1grew';
  ospId?: string;
  selectedOspId?: number;
  selectedJob?: OSP;

  newDailyReports: DailyReport[] = [new DailyReport()];

  selectedDate: string;
  serverDate: string;

  // LH-45 (2026-05-15): backdate limit is now Project-driven via OSP lookup fid 261.
  // - If selectedJob.limitWorkDate (fid 218 from Project fid 142) is true → today only.
  // - Otherwise use selectedJob.backdateLimitDays (Project fid 164, default 7); fall back to 7
  //   if the lookup returns null/undefined (i.e. Project never set the value, even though the
  //   field defaults to 7 — historical Projects created before LH-45 may have null).
  get minDate(): string {
    let date = new Date();
    if (this.selectedJob?.limitWorkDate) {
      return date.toISOString();
    }
    const days = this.selectedJob?.backdateLimitDays ?? 7;
    date.setDate(date.getDate() - days);
    return date.toISOString();
  }

  get maxDate(): string {
    let date = new Date();
    date.setDate(date.getDate() + 2);
    return date.toISOString();
  }

  get activeCrews(): Crew[] {
    if (!this.crewList) {
      return [];
    }

    return this.crewList.filter((crew) => crew.active);
  }

  canBeSaved(dr: DailyReport): boolean {
    return (
      !dr.saved &&
      (dr.qty || dr.qty == 0) &&
      (dr.cpBillingCode && dr.cpBillingCode.recordId > 0) &&
      dr.workDate &&
      dr.workDate.length != 0
    );
  }

  getJobName(dr: DailyReport): string {
    const filledJob = this.ospList?.find(
      (job) => job.recordId == dr.relatedOSP
    );
    return filledJob?.jobName;
  }

  getJobDisplayName(dr: DailyReport): string {
    const filledJob = this.ospList?.find(
      (job) => job.recordId == dr.relatedOSP
    );
    return filledJob?.displayName;
  }

  constructor(
    private dataService: DataService,
    private datePipe: DatePipe,
    private toastService: ToastService,
    private route: ActivatedRoute,
    private cd: ChangeDetectorRef,
    public modalController: ModalController,
    private activatedRoute: ActivatedRoute
  ) {
    this.route.params.subscribe((params) => {
      if (params['guid']) {
        this.guid = params['guid'];
        if (!this.guid) {
          return;
        }

        this.getCPData();
        this.getOSPList();
        this.getCrewList();
      }
    });

    this.activatedRoute.queryParams
      .pipe(take(1))
      .subscribe((params: Params) => {
        if (params['ospId']) {
          this.ospId = params['ospId'];
          this.selectedOspId = Number(this.ospId);
        }
      });
  }

  getOSPList() {
    this.dataService.getOspListByGuid(this.guid).subscribe(
      (data) => {
        if (!data) {
          return;
        }

        this.ospList = (data as []).map((osp) => new OSP(osp));

        if (!this.ospList.find((osp) => osp.recordId == this.selectedOspId)) {
          this.selectedOspId = undefined;
        }

        if (this.selectedOspId) {
          this.selectJob();
          this.getBillingCodes();
          this.getDailyReports();
          this.getTasks();
        }

        this.ospsLoaded = true;

        this.cd.markForCheck();
      },
      (error) => {
        console.log(error);

        this.toastService.presentToast(
          'Error',
          'Error while getting job list',
          'top',
          'danger',
          4000
        );
      }
    );
  }

  getCPData() {
    this.dataService.getCPDataByGuid(this.guid).subscribe(
      (data) => {
        if (!data) {
          return;
        }

        this.CPID = data[0][3].value;
        this.CPName = data[0][8].value;

        this.cd.markForCheck();
      },
      (error) => {
        console.log(error);

        this.toastService.presentToast(
          'Error',
          'Error while getting CP data',
          'top',
          'danger',
          4000
        );
      }
    );
  }

  getCrewList() {
    this.dataService.getCrewsByGuid(this.guid).subscribe((data) => {
      if (!data) {
        return;
      }

      this.crewList = (data as []).map((crew) => new Crew(crew));

      this.crewListLoaded = true;

      this.cd.markForCheck();
    });
  }

  getDailyReports() {
    this.dataService.getDRByGuid(this.guid, this.selectedOspId).subscribe(
      (data) => {
        if (!data) {
          return;
        }

        this.dailyReports = (data as []).map(
          (drData) => new DailyReport(drData)
        );

        this.drLoaded = true;

        this.cd.markForCheck();
      },
      (error) => {
        console.log(error);

        if(error && error.error && error.error.message && !(error.error.message as string).includes("Data not found"))
        this.toastService.presentToast(
          'Error',
          'Error while getting daily reports',
          'top',
          'danger',
          4000
        );
      }
    );
  }

  getBillingCodes() {
    this.dataService
      .getBillingCodesByGuid(this.guid, this.selectedOspId)
      .subscribe(
        (data) => {
          if (!data || !data['data']) {
            return;
          }

          this.billingCodes = (data['data'] as []).map(
            (bcData) => new CPBillingCode(bcData)
          );

          const sd = new Date(data['dateTime']);
          sd.setHours(sd.getHours() - 4);
          this.serverDate = sd.toISOString();

          if (this.selectedJob.limitWorkDate) {
            this.newDailyReports
              .filter((fdr) => !fdr.saved)
              .forEach((dr) => {
                dr.workDate = this.serverDate;
              });
          }

          this.bcLoaded = true;

          this.cd.markForCheck();
        },
        (error) => {
          console.log(error);

          this.toastService.presentToast(
            'Error',
            'Error while getting billing codes',
            'top',
            'danger',
            4000
          );
        }
      );
  }

  selectJob() {
    this.selectedJob = this.ospList.find(
      (job) => job.recordId == this.selectedOspId
    );

    if (this.selectedJob.limitWorkDate) {
      this.newDailyReports
        .filter((fdr) => !fdr.saved)
        .forEach((dr) => {
          dr.workDate = this.serverDate;
        });
    }

    this.checkDailyReports();
  }

  ngOnInit() {}

  checkSelectedJob(dr: DailyReport) {
    if (
      (dr.relatedOSP && dr.relatedOSP == this.selectedJob.recordId) ||
      !this.selectedJob
    ) {
      return;
    }

    dr.relatedOSP = this.selectedJob.recordId;
    dr.cpBillingCode = undefined;

    this.cd.markForCheck();
  }

  onCrewChange(event, i: number) {
    const selectedCrew = event.value as Crew;
    this.newDailyReports[i].crew = selectedCrew;
  }

  checkDailyReports() {
    this.newDailyReports
      .filter((dr) => !dr.saved && (!dr.relatedOSP || !dr.cpBillingCode))
      .forEach((emptyDR) => {
        emptyDR.relatedOSP = this.selectedJob.recordId;
        emptyDR.cpBillingCode = undefined;
      });
  }

  onJobChange(event) {
    this.selectedJob = event.value as JobDetails;
    this.selectedOspId = this.selectedJob.recordId;
    this.checkDailyReports();
    this.bcLoaded = false;
    this.drLoaded = false;
    this.cd.markForCheck();
    this.getDailyReports();
    this.getBillingCodes();
    this.getTasks();
  }

  onWorkDateChange(event, i: number) {
    this.newDailyReports[i].workDate = this.datePipe.transform(
      event.detail.value,
      'yyyy-MM-dd'
    );
  }

  transformDate(date: string) {
    const dateString = this.datePipe.transform(date, 'MM-dd-yyyy');
    return dateString;
  }

  onBillingCodeChange(event, i: number) {
    const selectedBillingCode = event.value as CPBillingCode;

    this.checkSelectedJob(this.newDailyReports[i]);

    this.newDailyReports[i].cpBillingCode = selectedBillingCode;
  }

  onAddDailyReportClick() {
    this.newDailyReports.push(new DailyReport());

    if (!this.selectedJob.limitWorkDate) {
      this.newDailyReports[this.newDailyReports.length - 1].workDate =
        this.newDailyReports[this.newDailyReports.length - 2].workDate;
    } else {
      this.newDailyReports[this.newDailyReports.length - 1].workDate =
        this.serverDate;
    }

    this.newDailyReports[this.newDailyReports.length - 1].relatedOSP =
      this.selectedJob?.recordId;

    this.cd.markForCheck();
  }

  getDailyReportShortDescription(dr: DailyReport): string {
    if (dr.finalBillingCodeDescription) {
      return dr.finalBillingCodeDescription;
    }

    if (dr.description) {
      return dr.description;
    }

    if (dr.cpBillingCodeShortDescription) {
      return dr.cpBillingCodeShortDescription;
    }

    return '';
  }

  onPostClick() {
    // The button is disabled in the template too, but Ionic's `disabled` is a
    // presentation concern — this is the real gate (LH-52 / LH-53).
    if (this.isPostBlocked()) {
      const reason = this.hasMissingRequiredAttachment()
        ? this.getMissingAttachmentMessages()[0]
        : this.getOverageMessages()[0];
      this.toastService.presentToast('Error', reason, 'top', 'danger', 6000);
      return;
    }

    const unsavedDRs = this.newDailyReports.filter((dr) => this.canBeSaved(dr));

    if (!unsavedDRs || unsavedDRs.length == 0) {
      this.toastService.presentToast(
        'Error',
        'Please fill in at least one Daily Report',
        'top',
        'danger',
        4000
      );
      return;
    }

    const postDRRequests = [];

    unsavedDRs.forEach((dr) => {
      postDRRequests.push(this.saveDailyReport(dr));
    });

    forkJoin(...postDRRequests).subscribe(
      (res) => {
        if (res && res.findIndex((r) => r == false) >= 0) {
          //There were an error during bulk send

          this.toastService.presentToast(
            'Error',
            'Error while saving',
            'top',
            'danger',
            4000
          );
        } else {
          this.toastService.presentToast(
            'Success',
            `Daily Report${unsavedDRs.length > 1 ? 's were' : ''} Saved`,
            'top',
            'success',
            4000
          );
        }

        this.cd.markForCheck();
        this.drLoaded = false;
        this.getDailyReports();
      },
      (error) => {
        console.log(error);

        this.toastService.presentToast(
          'Error',
          'Error while saving',
          'top',
          'danger',
          4000
        );
      }
    );
  }

  saveDailyReport(dr: DailyReport) {
    const postDRObservable = this.dataService
      .postDailyReport(dr.relatedOSP, {
        workDate: this.datePipe.transform(dr.workDate, 'yyyy-MM-dd'),
        selectedContractPartner: this.CPID,
        selectedBillingCode: dr.cpBillingCode?.recordId,
        relatedCrew: dr.crew?.rid,
        quantity: dr.qty,
        notes: dr.description,
      })
      .pipe(
        // LH-53: the created Daily Report's record id is already in the response
        // (create_daily_reports returns fid 3) but was being thrown away. We need
        // it to link the attachments, and the importer posts exactly one report
        // per HTTP call, so data[0] is unambiguously this report.
        switchMap((res) => {
          if (!res['success']) return of(false);

          dr.saved = true;
          dr.recordId = res['data']?.[0]?.['3']?.value ?? dr.recordId;

          return this.uploadAttachmentsFor(dr);
        })
      );
    return postDRObservable;
  }

  /**
   * LH-53 — upload a report's files AFTER it exists, so each File Attachment can
   * carry fid 18 "Related Daily Report".
   *
   * Uploading first was not an option: there is no attachment control on an
   * unsaved report today (the existing icon is hard-disabled with
   * `*ngIf="false && dr.saved"`), and a file with no drId would land unlinked.
   * So files are held on the DailyReport client-side and posted here, using the
   * same FormData shape as ModalUploaderComponent.confirm().
   *
   * A failed upload does NOT fail the report — the report is already saved in
   * QuickBase, and returning false here would tell the user "Error while saving"
   * about a record that exists. It surfaces as its own toast instead.
   */
  private uploadAttachmentsFor(dr: DailyReport) {
    const files = dr.attachments || [];
    if (!files.length || !dr.recordId) return of(true);

    const uploads = files.map((file) => {
      const formData = new FormData();
      formData.append(file.name, file);
      formData.append('ospId', String(dr.relatedOSP));
      formData.append('cpId', String(this.CPID));
      formData.append('drId', String(dr.recordId));
      formData.append('uploadedFromWhere', 'Field');
      formData.append('uploadingManagerId', 'Field');
      return this.dataService.postFile(dr.relatedOSP, formData);
    });

    return forkJoin(uploads).pipe(
      map(() => {
        dr.attachments = [];
        dr.withAttachments = true;
        return true;
      })
    );
  }

  presentModal(dr?: DailyReport) {
    let title = this.selectedJob.displayName;
    if (!dr) {
      this.dataService.getOspFiles(this.guid, this.selectedOspId).subscribe(
        (data) => {
          let files: FileMeta[] = [];
          (data as []).forEach((d) => {
            (d['data'] as []).forEach((item) => {
              files.push(new FileMeta(item));
            });
          });

          this.showPopup(title, files);
        },
        (error) => {
          console.log(error);

          this.toastService.presentToast(
            'Error',
            'Error while getting files',
            'top',
            'danger',
            4000
          );
        }
      );
    } else {
      title = `${title}>${this.transformDate(dr.workDate)} ${dr.qty} ${
        dr.code
      }`;

      if (dr.withAttachments) {
        this.dataService.getDRFiles(this.guid, dr.recordId).subscribe(
          (data) => {
            let files: FileMeta[] = [];
            (data as []).forEach((item) => {
              files.push(new FileMeta(item));
            });

            this.showPopup(title, files, dr.recordId);
          },
          (error) => {
            console.log(error);

            this.toastService.presentToast(
              'Error',
              'Error while getting files',
              'top',
              'danger',
              4000
            );
          }
        );
      } else {
        this.showPopup(title, [], dr.recordId);
      }
    }
  }

  async presentNotes(dr?: DailyReport) {
    const crew = this.crewList?.find(crew=>crew.crewId == dr.crewName);

    if(crew){
      dr.crewTitle = crew.crewTitle;
    }

    dr.workDateFormatted = this.transformDate(dr.workDate);
    
    const modal = await this.modalController.create({
      component: ModalDailyReportComponent,
      cssClass:"dr-modal",
      componentProps: {
        jobName: this.getJobDisplayName(dr),
        dr: dr,
        guid: this.guid,
        selectedOspId: this.selectedOspId,
        CPID: this.CPID
      },
    });
    modal.present();
    const { data, role } = await modal.onWillDismiss();
  }

  /**
   * LH-51: opens the partner's Contract Partner Purchase Order for the job that
   * is currently selected. Nothing new is plumbed in — the guid comes from the
   * route and the OSP id from the job picker, which together are exactly the
   * isolation key the backend scopes the purchase order with. The modal does its
   * own fetch, so this page (OnPush) gains no async state and needs no
   * markForCheck; the share gate is enforced server-side, never by an *ngIf.
   */
  async presentPurchaseOrder() {
    if (!this.guid || !this.selectedOspId) {
      this.toastService.presentToast(
        'Error',
        'Select a job before opening your Purchase Order',
        'top',
        'danger',
        4000
      );
      return;
    }

    const modal = await this.modalController.create({
      component: PoViewerComponent,
      cssClass: 'cp-po-modal',
      componentProps: {
        guid: this.guid,
        ospId: this.selectedOspId,
      },
    });
    modal.present();
    await modal.onWillDismiss();
  }

  async showPopup(title?: string, files?: any, drId?: number) {
    const modal = await this.modalController.create({
      component: ModalUploaderComponent,
      componentProps: {
        uploadedFiles: files.filter((f) => f.category != 'Client SOW'),
        drId: drId,
        ospId: this.selectedOspId,
        cpId: this.CPID,
        title: title,
      },
    });
    modal.present();
    const { data, role } = await modal.onWillDismiss();

    if (role === 'confirm') {
      this.toastService.presentToast(
        'Success',
        `Uploaded`,
        'top',
        'success',
        4000
      );
    }
    if (role === 'cancel' && data == 'error') {
      this.toastService.presentToast(
        'Error',
        'Error while saving',
        'top',
        'danger',
        4000
      );
    }

    this.getOSPList();
  }
  getTasks() {
    console.log('[OVERAGE DEBUG] getTasks() called, guid:', this.guid, 'ospId:', this.selectedOspId);
    this.dataService.getTasksByGuid(this.guid, this.selectedOspId).subscribe(
      (data) => {
        console.log('[OVERAGE DEBUG] getTasks() raw response (full):', JSON.stringify(data));
        if (!data) {
          console.log('[OVERAGE DEBUG] getTasks() no data returned');
          this.tasks = [];
          return;
        }
        const dataArray = data as any[];
        if (dataArray.length > 0) {
          console.log('[OVERAGE DEBUG] getTasks() first raw item ALL fields:', JSON.stringify(dataArray[0]));
        }
        this.tasks = dataArray.map((t) => new Task(t));
        console.log('[OVERAGE DEBUG] getTasks() parsed tasks:', this.tasks.map(t => ({
          rid: t.rid,
          planQty: t.planQty,
          relatedCPBillingCode: t.relatedCPBillingCode,
          CPBillingCode: t.CPBillingCode,
          CPBillingCodeDescription: t.CPBillingCodeDescription
        })));
        this.cd.markForCheck();
      },
      (error) => {
        console.log('[OVERAGE DEBUG] getTasks() ERROR:', error);
        this.tasks = [];
      }
    );
  }

  findTaskForBillingCode(billingCode: string): Task | undefined {
    if (!this.tasks || !billingCode) {
      console.log('[OVERAGE DEBUG] findTaskForBillingCode() early return - tasks:', this.tasks?.length, 'billingCode:', billingCode);
      return undefined;
    }
    const found = this.tasks.find(
      (task) => task.CPBillingCode == billingCode
    );
    console.log('[OVERAGE DEBUG] findTaskForBillingCode() looking for:', billingCode,
      '| available task codes:', this.tasks.map(t => t.CPBillingCode),
      '| found:', found ? { rid: found.rid, planQty: found.planQty, CPBillingCode: found.CPBillingCode } : 'NONE');
    return found;
  }

  getQuantityNumbers(dr: DailyReport): string {
    if (!dr.cpBillingCode) {
      return '';
    }

    const task = this.findTaskForBillingCode(dr.cpBillingCode.billingCode);
    if (!task || isNaN(task.planQty)) {
      console.log('[OVERAGE DEBUG] getQuantityNumbers() no task or no planQty for billingCode:', dr.cpBillingCode.billingCode,
        '| task:', task, '| dr.cpBillingCode:', { recordId: dr.cpBillingCode.recordId, billingCode: dr.cpBillingCode.billingCode });
      return '';
    }

    let savedQty = (this.dailyReports || [])
      .filter((existingDr) => existingDr.code == dr.cpBillingCode.billingCode)
      .map((filteredDr) => filteredDr.qty)
      .reduce((partSum, a) => partSum + a, 0);

    if (!isNaN(dr.qty)) savedQty += dr.qty;

    console.log('[OVERAGE DEBUG] getQuantityNumbers() result:', `${savedQty}/${task.planQty}`,
      '| savedDRs count:', (this.dailyReports || []).filter(d => d.code == dr.cpBillingCode.billingCode).length,
      '| currentQty:', dr.qty);

    return `${savedQty}/${task.planQty}`;
  }

  selectColor(dr: DailyReport): string {
    if (!dr.cpBillingCode) {
      return 'var(--ion-text-color, black)';
    }

    const task = this.findTaskForBillingCode(dr.cpBillingCode.billingCode);
    if (!task || isNaN(task.planQty)) {
      return 'var(--ion-text-color, black)';
    }

    let savedQty = (this.dailyReports || [])
      .filter((existingDr) => existingDr.code == dr.cpBillingCode.billingCode)
      .map((filteredDr) => filteredDr.qty)
      .reduce((partSum, a) => partSum + a, 0);

    if (!isNaN(dr.qty)) savedQty += dr.qty;

    // LH-52 (Damon 2026-09-08): strictly over plan is an overage; hitting plan
    // exactly is not. Kept in step with getOverages() below.
    if (savedQty > task.planQty) return 'crimson';

    return 'var(--ion-text-color, black)';
  }

  // -------------------------------------------------------------------------
  //  LH-52 — overage enforcement.
  //
  //  The arithmetic is unchanged (Tasks "Plan Qty" for the job vs already-posted
  //  quantities for this partner). Two things ARE different from selectColor(),
  //  deliberately, and both matter once POST can be blocked:
  //
  //  1. It aggregates every UNSAVED row in the batch that uses the same billing
  //     code. selectColor() counts only the row it is given, so a partner could
  //     split 120 over two rows against a plan of 100 and neither row would trip
  //     — which would make the block trivially bypassable.
  //  2. It is computed once per code rather than per row, so one message is
  //     produced per offending code instead of one per row.
  //
  //  The comparison is STRICT: only reported > planned is an overage. Damon
  //  confirmed 2026-09-08 that hitting plan exactly must neither warn nor block
  //  (the pre-LH-52 code treated >= as an overage; that was never intended).
  // -------------------------------------------------------------------------
  private static readonly OVERAGE_IS_STRICT = true;

  /** True when this project's Manager has ticked "Enforce Daily Report Overage Limit". */
  isOverageEnforced(): boolean {
    return !!this.selectedJob?.enforceOverageLimit;
  }

  /** One entry per offending billing code: {code, reported, planned}. */
  private getOverages(): { code: string; reported: number; planned: number }[] {
    const pending = this.newDailyReports.filter(
      (dr) => !dr.saved && dr.cpBillingCode && dr.cpBillingCode.billingCode
    );

    // Sum the pending batch per billing code first, so a code split across
    // several rows is measured once, as a total.
    const pendingByCode = new Map<string, number>();
    pending.forEach((dr) => {
      const code = dr.cpBillingCode.billingCode;
      const qty = isNaN(dr.qty) || dr.qty == null ? 0 : Number(dr.qty);
      pendingByCode.set(code, (pendingByCode.get(code) || 0) + qty);
    });

    const overages: { code: string; reported: number; planned: number }[] = [];

    pendingByCode.forEach((pendingQty, code) => {
      const task = this.findTaskForBillingCode(code);
      // No Task row for this code means there is no plan to exceed. Fail OPEN:
      // never block on a quantity we cannot compare. 85% of DEV jobs have no
      // Task rows at all, so blocking here would be blocking on missing data.
      if (!task || isNaN(task.planQty)) return;

      const postedQty = (this.dailyReports || [])
        .filter((existingDr) => existingDr.code == code)
        .map((filteredDr) => filteredDr.qty)
        .reduce((partSum, a) => partSum + a, 0);

      const reported = postedQty + pendingQty;
      const isOver = DailyImporterV3Page.OVERAGE_IS_STRICT
        ? reported > task.planQty
        : !(task.planQty > reported);

      if (isOver) overages.push({ code, reported, planned: task.planQty });
    });

    return overages;
  }

  hasAnyOverage(): boolean {
    return this.getOverages().length > 0;
  }

  getOverageMessages(): string[] {
    const blocking = this.isOverageEnforced();

    return this.getOverages().map(
      ({ code, reported, planned }) =>
        `Billing code ${code} exceeds planned quantity ` +
        `(reported: ${reported}, planned: ${planned}). ` +
        (blocking
          ? 'Submission is blocked for this project. Please adjust the quantity ' +
            'or contact your Lighthouse manager.'
          : 'You can still submit, but the report may not be approved. ' +
            'Please contact your Lighthouse manager.')
    );
  }

  // -------------------------------------------------------------------------
  //  LH-53 — required attachments.
  // -------------------------------------------------------------------------

  /** True when the row's billing code is flagged "Attachment Required". */
  requiresAttachment(dr: DailyReport): boolean {
    return !!dr?.cpBillingCode?.attachmentRequired;
  }

  /** Rows that need a file and do not have one yet. */
  private getReportsMissingAttachment(): DailyReport[] {
    return this.newDailyReports.filter(
      (dr) =>
        !dr.saved &&
        this.canBeSaved(dr) &&
        this.requiresAttachment(dr) &&
        !(dr.attachments && dr.attachments.length)
    );
  }

  hasMissingRequiredAttachment(): boolean {
    return this.getReportsMissingAttachment().length > 0;
  }

  /**
   * LH-53: what to tell the partner for this row. The manager can set a
   * per-billing-code message on Billing Codes ("Attachment Required Message",
   * e.g. "Rock adder requires pictures and videos of rock every 150 ft");
   * when present it replaces the generic sentence.
   */
  attachmentRequirementText(dr: DailyReport): string {
    const custom = (dr?.cpBillingCode?.attachmentRequiredMessage || '').trim();
    if (custom) return custom;
    return (
      `An attachment is required for billing code ` +
      `${dr?.cpBillingCode?.billingCode}. Please upload a file before ` +
      `posting this report.`
    );
  }

  getMissingAttachmentMessages(): string[] {
    return [
      ...new Set(
        this.getReportsMissingAttachment().map((dr) => this.attachmentRequirementText(dr))
      ),
    ];
  }

  onAttachmentSelected(event: any, dr: DailyReport) {
    const picked: FileList = event?.target?.files;
    if (!picked || !picked.length) return;

    dr.attachments = [...(dr.attachments || []), ...Array.from(picked)];
    // Let the same input be used again for the same file name.
    if (event.target) event.target.value = '';
    this.cd.markForCheck();
  }

  removeAttachment(dr: DailyReport, file: File) {
    dr.attachments = (dr.attachments || []).filter((f) => f !== file);
    this.cd.markForCheck();
  }

  /**
   * The single POST gate. Both tickets add a blocking condition and the button
   * had none before, so this is also the first thing that ever disabled it.
   */
  isPostBlocked(): boolean {
    return (
      (this.isOverageEnforced() && this.hasAnyOverage()) ||
      this.hasMissingRequiredAttachment()
    );
  }

  getOspAttachmentColor() {
    if (this.selectedJob?.withAttachments) return 'darkblue';

    return '';
  }

  getDRAttachmentColor(dr: DailyReport) {
    if (dr?.withAttachments) return 'darkblue';

    return '';
  }
}
